// @ts-strict-ignore
import { getLastFocusableElement } from "@tablescape/utilities";
import { component } from "picoapp";

export const desktopNavigation = component((node: HTMLElement) => {
  const navMainLinkElements =
    node.querySelectorAll<HTMLElement>(".js-main-nav-link");
  const desktopDropdownContentElements = node.querySelectorAll<HTMLElement>(
    ".js-desktop-nav-dropdown-content"
  );
  const overlay = document.querySelector(".js-desktop-overlay");

  let openDesktopNav = null;

  // Handlers
  const toggleDesktopDropdown = (
    navDropdown: HTMLElement,
    open: boolean,
    animate = true
  ) => {
    if (!animate) {
      navDropdown.classList.remove("transition-height");
      navDropdown.classList.remove("duration-200");
    } else {
      navDropdown.classList.add("transition-height");
      navDropdown.classList.add("duration-200");
    }

    if (open) {
      navDropdown.style.height = "402px";
      navDropdown.removeAttribute("aria-hidden");
      overlay.classList.add("mg:block");
      navDropdown.classList.remove("invisible");
    } else {
      navDropdown.style.height = "0px";
      navDropdown.ariaHidden = "true";
      overlay.classList.remove("mg:block");
      navDropdown.classList.add("invisible");
    }
  };

  const openDesktopDropdownHandler = (e: KeyboardEvent | MouseEvent) => {
    if (!(e.currentTarget instanceof HTMLElement)) {
      return;
    }

    const desktopNavDropdown = node.querySelector<HTMLElement>(
      `.js-desktop-nav-dropdown[data-dropdown-id=${e.currentTarget.dataset.dropdownId}]`
    );

    if (!openDesktopNav) {
      toggleDesktopDropdown(desktopNavDropdown, true);
    } else {
      toggleDesktopDropdown(desktopNavDropdown, true, false);
    }

    openDesktopNav = e.currentTarget.dataset.dropdownId;
  };

  const closeDesktopDropdownHandler = (e: KeyboardEvent | MouseEvent) => {
    if (!(e.currentTarget instanceof HTMLElement)) {
      return;
    }

    const desktopNavDropdown = node.querySelector<HTMLElement>(
      `.js-desktop-nav-dropdown[data-dropdown-id=${e.currentTarget.dataset.dropdownId}]`
    );

    if (
      e instanceof MouseEvent &&
      e.relatedTarget instanceof HTMLElement &&
      [...navMainLinkElements, ...desktopDropdownContentElements].includes(
        e.relatedTarget
      )
    ) {
      toggleDesktopDropdown(desktopNavDropdown, false, false);
    } else {
      toggleDesktopDropdown(desktopNavDropdown, false);
      openDesktopNav = null;
    }
  };

  const closeAllDesktopDropdownHandler = () => {
    const allDesktopNavDropdowns = node.querySelectorAll<HTMLElement>(
      ".js-desktop-nav-dropdown"
    );

    allDesktopNavDropdowns.forEach((navDropdown) =>
      toggleDesktopDropdown(navDropdown, false, false)
    );
  };

  const desktopDropdownTabbingHandler = (e: KeyboardEvent) => {
    if (e.key === "Enter" && e.currentTarget instanceof HTMLElement) {
      e.preventDefault();
      const desktopNavDropdown = node.querySelector(
        `.js-desktop-nav-dropdown[data-dropdown-id=${e.currentTarget.dataset.dropdownId}]`
      );

      if (desktopNavDropdown.getAttribute("aria-hidden") === "true") {
        closeAllDesktopDropdownHandler();
        openDesktopDropdownHandler(e);
      } else {
        closeDesktopDropdownHandler(e);
      }
    }
  };

  const dropdownFocusHandler = (
    e: KeyboardEvent & { currentTarget: HTMLElement }
  ) => {
    const dropdownLastFocusElement = getLastFocusableElement(e.currentTarget);
    if (
      (e.key === "Tab" &&
        !e.shiftKey &&
        e.target === dropdownLastFocusElement) ||
      e.key === "Escape"
    ) {
      closeDesktopDropdownHandler(e);

      if (e.key === "Escape") {
        // Return focus to link when pressing escape
        const dropdownId = e.currentTarget.dataset.dropdownId;
        if (dropdownId) {
          const activeMenuLink = [...navMainLinkElements].find(
            (link) => link.dataset.dropdownId === dropdownId
          );
          activeMenuLink.focus();
        }
      }
    }
  };

  // Setup Listeners

  [...navMainLinkElements, ...desktopDropdownContentElements].forEach(
    (link) => {
      link.addEventListener("mouseenter", openDesktopDropdownHandler);
      link.addEventListener("mouseleave", closeDesktopDropdownHandler);
    }
  );

  desktopDropdownContentElements.forEach((dropdown) => {
    dropdown.addEventListener("keydown", dropdownFocusHandler);
  });

  navMainLinkElements.forEach((link) =>
    link.addEventListener("keydown", desktopDropdownTabbingHandler)
  );

  return () => {
    [...navMainLinkElements, ...desktopDropdownContentElements].forEach(
      (link) => {
        link.removeEventListener("mouseenter", openDesktopDropdownHandler);
        link.removeEventListener("mouseleave", closeDesktopDropdownHandler);
      }
    );
    navMainLinkElements.forEach((link) => {
      link.removeEventListener("click", desktopDropdownTabbingHandler);
    });
    desktopDropdownContentElements.forEach((dropdown) => {
      dropdown.removeEventListener("keydown", dropdownFocusHandler);
    });
  };
});
